import type { MediaItem, MediaType } from "@/types/media";
import { movies } from "@/mocks/movies";
import { tvshows } from "@/mocks/tvshows";
import { collections } from "@/mocks/collections";

export const movieList = movies as MediaItem[];
export const tvList = tvshows as MediaItem[];
export const allMedia: MediaItem[] = [...movieList, ...tvList];
export const collectionList = collections;

export const genreOptions = ["动作", "科幻", "悬疑", "喜剧", "剧情", "爱情", "奇幻", "古装", "武侠", "战争", "青春", "职场"];
export const regionOptions = ["大陆", "欧美", "日韩"];
export const yearOptions = ["2025", "2024", "2023", "2022"];

export interface CategoryFilters {
  genre: string;
  region: string;
  year: string;
  rating: string;
  sort: string;
}

export const defaultFilters: CategoryFilters = {
  genre: "全部",
  region: "全部",
  year: "全部",
  rating: "全部",
  sort: "latest",
};

export function getMediaById(type: MediaType, id: string): MediaItem | undefined {
  const source = type === "movie" ? movieList : tvList;
  return source.find((item) => item.id === id);
}

export function getFeatured(list: MediaItem[] = allMedia): MediaItem[] {
  return list.filter((item) => item.featured);
}

export function getLatestMovies(count = 8): MediaItem[] {
  return [...movieList].sort((a, b) => b.year - a.year).slice(0, count);
}

export function getHotTvShows(count = 8): MediaItem[] {
  return [...tvList].sort((a, b) => b.views - a.views).slice(0, count);
}

export function filterMedia(list: MediaItem[], filters: CategoryFilters): MediaItem[] {
  let result = list.filter((item) => {
    const genreOk = filters.genre === "全部" || item.genres.includes(filters.genre);
    const regionOk = filters.region === "全部" || item.region === filters.region;
    const yearOk = filters.year === "全部" || String(item.year) === filters.year;
    const ratingOk = filters.rating === "全部" || item.rating >= Number(filters.rating);
    return genreOk && regionOk && yearOk && ratingOk;
  });

  if (filters.sort === "rating") {
    result = [...result].sort((a, b) => b.rating - a.rating);
  } else if (filters.sort === "views") {
    result = [...result].sort((a, b) => b.views - a.views);
  } else {
    result = [...result].sort((a, b) => b.year - a.year);
  }

  return result;
}

export function searchMedia(keyword: string, type: MediaType | "all"): MediaItem[] {
  const kw = keyword.trim().toLowerCase();
  if (!kw) return [];
  const source = type === "all" ? allMedia : type === "movie" ? movieList : tvList;
  return source.filter((item) => {
    return (
      item.title.toLowerCase().includes(kw) ||
      item.genres.some((g) => g.toLowerCase().includes(kw)) ||
      item.region.toLowerCase().includes(kw) ||
      item.cast.some((c) => c.toLowerCase().includes(kw)) ||
      item.director.toLowerCase().includes(kw) ||
      item.tags.some((t) => t.toLowerCase().includes(kw))
    );
  });
}

export function getRelated(item: MediaItem, count = 6): MediaItem[] {
  const source = item.type === "movie" ? movieList : tvList;
  const scored = source
    .filter((m) => m.id !== item.id)
    .map((m) => {
      const score = m.genres.filter((g) => item.genres.includes(g)).length * 2 +
        m.tags.filter((t) => item.tags.includes(t)).length;
      return { item: m, score };
    })
    .sort((a, b) => b.score - a.score);
  return scored.slice(0, count).map((s) => s.item);
}

export function formatViews(views: number): string {
  if (views >= 10000) return `${(views / 10000).toFixed(1)}万`;
  return String(views);
}

export function buildEpisodes(item: MediaItem): { index: number; title: string }[] {
  const total = item.episodeCount ?? 24;
  const list: { index: number; title: string }[] = [];
  for (let i = 1; i <= total; i += 1) {
    list.push({ index: i, title: `第 ${i} 集` });
  }
  return list;
}